// src/views/dashboard/Dashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import dashboardService  from "../../services/dashboardService";
import { useAuth }        from "../../contexts/AuthContext";
const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtN = v => Number(v||0).toLocaleString("pt-BR");
const fmtD = d => { try{ return new Date((String(d||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||"—"; } };

// ── Mini Sparkline Bar Chart ───────────────────────────────────────────────
function BarChart({ data, height=60 }) {
  if (!data?.length) return null;
  const maxVal = Math.max(...data.map(d => Math.max(d.receitas, d.despesas)), 1);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:3, height, paddingTop:4 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
          <div style={{ width:"100%", display:"flex", gap:1, alignItems:"flex-end", height:height-14 }}>
            <div style={{ flex:1, background:"#3B82F6", borderRadius:"2px 2px 0 0", opacity:.85,
              height: `${(d.receitas/maxVal)*100}%`, minHeight: d.receitas>0?2:0, transition:"height .3s" }}/>
            <div style={{ flex:1, background:"#EF4444", borderRadius:"2px 2px 0 0", opacity:.75,
              height: `${(d.despesas/maxVal)*100}%`, minHeight: d.despesas>0?2:0, transition:"height .3s" }}/>
          </div>
          <div style={{ fontSize:9, color:"var(--gray-400)", whiteSpace:"nowrap" }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Gauge de adimplência ──────────────────────────────────────────────────
function Gauge({ pct }) {
  const v = parseFloat(pct || 0);
  const color = v >= 90 ? "#22C55E" : v >= 70 ? "#F59E0B" : "#EF4444";
  const r = 36, circ = 2 * Math.PI * r;
  const offset = circ - (v / 100) * circ;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width={90} height={90} viewBox="0 0 90 90">
        <circle cx={45} cy={45} r={r} fill="none" stroke="var(--gray-100)" strokeWidth={8}/>
        <circle cx={45} cy={45} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 45 45)"
          style={{ transition:"stroke-dashoffset .6s ease" }}/>
        <text x={45} y={49} textAnchor="middle" fontSize={14} fontWeight={700} fill={color}>{v}%</text>
      </svg>
      <div style={{ fontSize:11, color:"var(--gray-500)", fontWeight:600 }}>Adimplência</div>
    </div>
  );
}

// ── Indicador de delta (variação % vs mês anterior) ──────────────────────
// invertido=true para métricas em que "subir" é ruim (ex.: inadimplência).
function DeltaTag({ pct, invertido }) {
  if (pct === null || pct === undefined || isNaN(pct)) return null;
  const up = pct >= 0;
  const isGood = invertido ? !up : up;
  const color  = pct === 0 ? "#64748B" : isGood ? "#166534" : "#991B1B";
  const bg     = pct === 0 ? "#F1F5F9" : isGood ? "#DCFCE7" : "#FEE2E2";
  const arrow  = pct === 0 ? "→" : up ? "▲" : "▼";
  return (
    <span title="vs mês anterior"
      style={{ background:bg, color, fontSize:10.5, fontWeight:700,
        padding:"2px 6px", borderRadius:6, display:"inline-flex", alignItems:"center", gap:3, lineHeight:1 }}>
      {arrow} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

// ── Card KPI ──────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color="#1B3A6B", bg="#EFF6FF", onClick, badge, loading, delta, deltaInvertido, alert }) {
  return (
    <div onClick={onClick}
      style={{ background:"var(--bg-surface)", borderRadius:12, padding:"16px 18px",
        border:`1px solid ${alert?"#FECACA":"var(--gray-200)"}`,
        cursor:onClick?"pointer":"default",
        transition:"box-shadow .15s", boxShadow:"0 1px 3px rgba(0,0,0,.06)" }}
      onMouseEnter={e=>onClick&&(e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.10)")}
      onMouseLeave={e=>onClick&&(e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,.06)")}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
        <div style={{ width:40, height:40, borderRadius:10, background:bg,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <i className={`bi ${icon}`} style={{ color, fontSize:19 }}/>
        </div>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {delta != null && <DeltaTag pct={delta} invertido={deltaInvertido}/>}
          {badge!=null && (
            <span style={{ background:bg, color, fontSize:11, fontWeight:700,
              padding:"2px 8px", borderRadius:10 }}>{badge}</span>
          )}
        </div>
      </div>
      {loading
        ? <div style={{ height:28, background:"var(--gray-100)", borderRadius:6, marginBottom:4, animation:"pulse 1.5s infinite" }}/>
        : <div style={{ fontSize:22, fontWeight:800, color:"var(--gray-800)", lineHeight:1.2 }}>{value}</div>
      }
      <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:3 }}>{label}</div>
      {sub && <div style={{ fontSize:11.5, color, fontWeight:600, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Seção com título ──────────────────────────────────────────────────────
function Section({ title, icon, children, action }) {
  return (
    <div style={{ background:"var(--bg-surface)", borderRadius:12, padding:"16px 18px",
      border:"1px solid var(--gray-200)", boxShadow:"0 1px 3px rgba(0,0,0,.06)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <i className={`bi ${icon}`} style={{ color:"var(--primary)", fontSize:16 }}/>
          <span style={{ fontWeight:700, fontSize:13.5, color:"var(--gray-800)" }}>{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function Dashboard({ onNavigate }) {
  const { condominioAtivo, trocarCondominio, usuario, condominiosDisponiveis, isAdmin, isSuperAdmin, avisoCondoSemAcesso, setAvisoCondoSemAcesso } = useAuth();
  // Fonte única: lista vem do AuthContext (atualiza ao criar/editar condomínio)
  const condominios = condominiosDisponiveis || [];
  const [idCondo, setIdCondo]         = useState("");
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [lastUpdate, setLastUpdate]   = useState(null);
  const [onbSteps, setOnbSteps]       = useState([false,false,false,false,false]);

  // Sincroniza o condomínio ativo do contexto (atualiza sempre que mudar pela navbar)
  useEffect(() => {
    setIdCondo(condominioAtivo?.id ? String(condominioAtivo.id) : "");
  }, [condominioAtivo]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await dashboardService.resumo(idCondo);
      setData(r);
      // Mark onboarding steps as done
      setOnbSteps([
        true, // condomínio já existe (selecionamos um)
        (r.contasBancarias?.length || 0) > 0,
        (r.unidades?.total || 0) > 0,
        (r.unidades?.ocupadas || 0) > 0,
        (r.boletosDoMes?.total || 0) > 0,
      ]);
      setLastUpdate(new Date().toLocaleTimeString("pt-BR"));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [idCondo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Auto-refresh a cada 5 minutos
  useEffect(() => {
    const t = setInterval(carregar, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [carregar]);

  const condoNome = condominioAtivo?.nmFantasia
    || condominioAtivo?.nmCondominio
    || condominios.find(c => String(c.id) === String(idCondo))?.nmFantasia
    || condominios.find(c => String(c.id) === String(idCondo))?.nmCondominio
    || "";

  const mesLabel = data?.mesAtual
    ? new Date(data.mesAtual + "-01").toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    : "";

  const pctOcupacao = data?.unidades?.total > 0
    ? Math.round((data.unidades.ocupadas / data.unidades.total) * 100)
    : 0;

  return (
    <div>
      {!idCondo && (
        <div style={{padding:"40px 20px"}}>
          {condominios.length === 0 ? (
            /* ── ONBOARDING: nenhum condomínio cadastrado ── */
            <div style={{maxWidth:680,margin:"0 auto"}}>
              <div style={{background:"var(--bg-surface)",borderRadius:16,padding:"40px 36px",
                boxShadow:"0 4px 24px rgba(0,0,0,.08)",marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
                  <div style={{width:56,height:56,borderRadius:14,background:"var(--bg-highlight)",
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <i className="bi bi-stars" style={{fontSize:28,color:"var(--primary)"}}/>
                  </div>
                  <div>
                    <h2 style={{margin:0,fontSize:20,fontWeight:700,color:"var(--text-primary)"}}>
                      Bem-vindo ao Horizon eCondo! 🎉
                    </h2>
                    <p style={{margin:0,fontSize:13,color:"var(--gray-500)",marginTop:3}}>
                      Siga os passos abaixo para configurar seu sistema
                    </p>
                  </div>
                </div>

                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {[
                    {step:1,icon:"bi-buildings",title:"Cadastre seu condomínio",
                     desc:"Informe nome, CNPJ, endereço e configurações financeiras",
                     btn:"Cadastrar Condomínio", nav:"condominios", done:onbSteps[0]},
                    {step:2,icon:"bi-bank",title:"Configure a conta bancária",
                     desc:"Necessário para geração de boletos e controle financeiro",
                     btn:"Configurar Conta", nav:"contasBancarias", done:onbSteps[1]},
                    {step:3,icon:"bi-house-door",title:"Cadastre as unidades",
                     desc:"Apartamentos, casas ou salas do condomínio",
                     btn:"Cadastrar Unidades", nav:"unidades", done:onbSteps[2]},
                    {step:4,icon:"bi-people",title:"Cadastre os condôminos",
                     desc:"Proprietários e inquilinos de cada unidade",
                     btn:"Cadastrar Condôminos", nav:"condominos", done:onbSteps[3]},
                    {step:5,icon:"bi-receipt",title:"Gere sua primeira cobrança",
                     desc:"Crie e envie boletos mensais para todos os condôminos",
                     btn:"Gerar Cobranças", nav:"cobrancas", done:onbSteps[4]},
                  ].map(s=>(
                    <div key={s.step} style={{display:"flex",alignItems:"center",gap:14,
                      padding:"14px 16px",borderRadius:10,border:"1px solid var(--border-color)",
                      background:"var(--bg-surface-alt)"}}>
                      <div style={{width:36,height:36,borderRadius:10,
                        background:s.done?"#DCFCE7":"#EFF6FF",
                        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <i className={`bi ${s.done?"bi-check-circle-fill":"bi-circle"}`}
                          style={{fontSize:18,color:s.done?"#166534":"var(--primary)"}}/>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>
                          {s.step}. {s.title}
                        </div>
                        <div style={{fontSize:11,color:"var(--gray-500)"}}>{s.desc}</div>
                      </div>
                      <button className="btn btn-primary btn-sm"
                        onClick={()=>onNavigate?.(s.nav)}
                        style={{fontSize:12,flexShrink:0}}>
                        {s.btn} →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* ── Seletor de condomínio (ou CTA quando lista vazia) ── */
            <div style={{textAlign:"center"}}>
              {avisoCondoSemAcesso && (
                <div style={{
                  maxWidth:520, margin:"0 auto 16px",
                  background:"#FEF3C7", border:"1px solid #FDE68A", color:"#92400E",
                  borderRadius:8, padding:"10px 14px", fontSize:13.5,
                  display:"flex", alignItems:"center", gap:10,
                }}>
                  <i className="bi bi-exclamation-triangle" style={{fontSize:18}}/>
                  <div style={{flex:1, textAlign:"left"}}>{avisoCondoSemAcesso}</div>
                  <button className="btn btn-sm" style={{padding:"2px 8px",color:"#92400E"}} onClick={()=>setAvisoCondoSemAcesso("")}>
                    <i className="bi bi-x-lg"/>
                  </button>
                </div>
              )}

              {condominios.length === 0 ? (
                <div style={{background:"var(--bg-surface)",borderRadius:16,padding:"48px 32px",
                  maxWidth:520,margin:"0 auto",boxShadow:"0 4px 24px rgba(0,0,0,.08)"}}>
                  <i className="bi bi-building-exclamation" style={{fontSize:64,color:"#F59E0B",
                    display:"block",marginBottom:16}}/>
                  <h2 style={{fontSize:22,fontWeight:700,color:"var(--gray-800)",marginBottom:8}}>
                    Nenhum condomínio disponível
                  </h2>
                  {usuario?.perfil === "Sindico" ? (
                    <p style={{fontSize:14,color:"var(--gray-500)",marginBottom:8}}>
                      Você ainda não está vinculado a nenhum condomínio. Solicite ao administrador.
                    </p>
                  ) : (
                    <>
                      <p style={{fontSize:14,color:"var(--gray-500)",marginBottom:24}}>
                        Você ainda não tem condomínios cadastrados. Cadastre o primeiro para começar.
                      </p>
                      {(isAdmin || isSuperAdmin) && (
                        <button className="btn btn-primary btn-lg" onClick={()=>onNavigate?.("condominios")}>
                          <i className="bi bi-plus-lg me-2"/>Cadastrar primeiro condomínio
                        </button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div style={{background:"var(--bg-surface)",borderRadius:16,padding:"48px 32px",
                  maxWidth:520,margin:"0 auto",boxShadow:"0 4px 24px rgba(0,0,0,.08)"}}>
                  <i className="bi bi-buildings" style={{fontSize:64,color:"var(--primary)",
                    display:"block",marginBottom:16}}/>
                  <h2 style={{fontSize:22,fontWeight:700,color:"var(--gray-800)",marginBottom:8}}>
                    Selecione o condomínio
                  </h2>
                  <p style={{fontSize:14,color:"var(--gray-500)",marginBottom:28}}>
                    Escolha o condomínio que deseja gerenciar para ver o dashboard.
                  </p>
                  <select className="form-select form-select-lg"
                    value={idCondo} onChange={e=>{
                      setIdCondo(e.target.value);
                      const condo=condominios.find(cc=>String(cc.id)===e.target.value);
                      if(condo) trocarCondominio(condo);
                    }}
                    style={{maxWidth:360,margin:"0 auto",fontSize:15,
                      border:"2px solid var(--primary)",borderRadius:10}}>
                    <option value="">-- Selecione um condomínio --</option>
                    {condominios.map(cc=>(
                      <option key={cc.id} value={cc.id}>{cc.nmFantasia||cc.nmCondominio}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {idCondo && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* ── Header ── */}
          <div className="page-header" style={{ marginBottom:4 }}>
            <div>
              <h1 className="page-title">
                <i className="bi bi-speedometer2 me-2" style={{ color:"var(--primary)", fontSize:22 }}/>
                Dashboard
              </h1>
              <p className="page-subtitle">
                {mesLabel && `Competência: ${mesLabel}`}
                {lastUpdate && <span style={{ marginLeft:12, fontSize:11, color:"var(--gray-400)" }}>
                  <i className="bi bi-clock me-1"/>Atualizado às {lastUpdate}
                </span>}
              </p>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <button className="btn btn-outline-secondary btn-sm" onClick={carregar} disabled={loading}
                title="Atualizar">
                <i className={`bi bi-arrow-clockwise ${loading?"spin":""}`}/>
              </button>
            </div>
          </div>

          {/* ── Banner síndico / boas-vindas ── */}
          {usuario && (
            <div style={{background:"linear-gradient(135deg,#1B3A6B 0%,#2563EB 100%)",
              borderRadius:14,padding:"20px 24px",display:"flex",
              justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{color:"rgba(255,255,255,.7)",fontSize:12,marginBottom:2}}>
                  {new Date().toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                </div>
                <div style={{color:"#fff",fontSize:18,fontWeight:700}}>
                  Olá, {usuario.nome?.split(" ")[0]}! 👋
                </div>
                <div style={{color:"rgba(255,255,255,.8)",fontSize:13,marginTop:2}}>
                  {{Admin:"Administrador",Gerente:"Gerente",Sindico:"Síndico",Condomino:"Condômino"}[usuario.perfil]||"Usuário"}
                  {data?.condo?.nmFantasia ? ` — ${data.condo.nmFantasia}` : ""}
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[
                  {label:"Boletos",   icon:"bi-receipt-cutoff",  nav:"cobrancas"},
                  {label:"Relatórios",icon:"bi-bar-chart-line",  nav:"relatorios"},
                  {label:"Assembleias",icon:"bi-people",         nav:"assembleias"},
                  {label:"Ocorrências",icon:"bi-exclamation-circle",nav:"ocorrencias"},
                ].map(q=>(
                  <button key={q.nav}
                    onClick={()=>onNavigate?.(q.nav)}
                    style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.25)",
                      color:"#fff",borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer",
                      display:"flex",alignItems:"center",gap:5,fontWeight:600,
                      transition:"background .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.25)"}
                    onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.15)"}>
                    <i className={`bi ${q.icon}`}/>{q.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Setup progress (se incompleto) ── */}
          {data && (() => {
            const concluidos = onbSteps.filter(Boolean).length;
            if (concluidos >= 5) return null;
            return (
              <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",
                borderRadius:12,padding:"14px 18px",
                display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                <i className="bi bi-rocket-takeoff-fill" style={{fontSize:22,color:"#C2410C"}}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13,color:"#92400E"}}>
                    Configure seu sistema — {concluidos} de 5 passos concluídos
                  </div>
                  <div style={{height:6,background:"#FED7AA",borderRadius:3,marginTop:6,maxWidth:300}}>
                    <div style={{height:"100%",borderRadius:3,background:"#F97316",
                      width:`${(concluidos/5)*100}%`,transition:"width .5s"}}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[
                    {i:1,done:onbSteps[1],label:"Conta Bancária",nav:"contasBancarias"},
                    {i:2,done:onbSteps[2],label:"Unidades",nav:"unidades"},
                    {i:3,done:onbSteps[3],label:"Condôminos",nav:"condominos"},
                    {i:4,done:onbSteps[4],label:"Cobranças",nav:"cobrancas"},
                  ].filter(s=>!s.done).slice(0,2).map(s=>(
                    <button key={s.nav}
                      onClick={()=>onNavigate?.(s.nav)}
                      style={{background:"#C2410C",color:"#fff",border:"none",
                        borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer",
                        fontWeight:600}}>
                      {s.label} →
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── LINHA 1 — KPIs principais ── */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            <KpiCard icon="bi-house-door" label="Unidades" loading={loading}
              value={fmtN(data?.unidades?.total)}
              sub={`${data?.unidades?.ocupadas || 0} ocupadas · ${data?.unidades?.vagas || 0} vagas`}
              badge={`${pctOcupacao}%`}
              color="#1D4ED8" bg="#EFF6FF"
              onClick={() => onNavigate?.("unidades")}/>

            <KpiCard icon="bi-bank" label="Saldo Total (Contas)" loading={loading}
              value={fmtM(data?.saldoTotal)}
              sub={`${data?.contasBancarias?.length || 0} conta(s) ativa(s)`}
              color="#166534" bg="#DCFCE7"
              onClick={() => onNavigate?.("contasBancarias")}/>

            <KpiCard icon="bi-arrow-down-circle" label="A Receber (Mês)" loading={loading}
              value={fmtM(data?.contasReceber?.vlrEmAberto)}
              sub={(() => {
                const cr = data?.contasReceber || {};
                const abertos = cr.emAberto || 0;
                // Aproximação: usamos boletos vencidos do mês como proxy quando contasReceber.vencidas não vem
                const vencidos = data?.boletosDoMes?.vencidos || 0;
                return vencidos > 0
                  ? `${abertos} em aberto · ${vencidos} vencido(s)`
                  : `${abertos} lançamento(s) em aberto`;
              })()}
              color={(data?.boletosDoMes?.vencidos||0) > 0 ? "#991B1B" : "#6D28D9"}
              bg={(data?.boletosDoMes?.vencidos||0) > 0 ? "#FEE2E2" : "#F5F3FF"}
              alert={(data?.boletosDoMes?.vencidos||0) > 0}
              delta={data?.comparativo?.receitas?.deltaPct}
              onClick={() => onNavigate?.("contasReceber")}/>

            <KpiCard icon="bi-arrow-up-circle" label="A Pagar (Mês)" loading={loading}
              value={fmtM(data?.contasPagar?.vlrEmAberto)}
              sub={`${data?.contasPagar?.vencidas || 0} vencida(s)`}
              color={data?.contasPagar?.vencidas > 0 ? "#991B1B" : "#C2410C"}
              bg={data?.contasPagar?.vencidas > 0 ? "#FEE2E2" : "#FFF7ED"}
              alert={data?.contasPagar?.vencidas > 0}
              delta={data?.comparativo?.despesas?.deltaPct} deltaInvertido
              onClick={() => onNavigate?.("contasPagar")}/>
          </div>

          {/* ── LINHA 2 — Boletos + Adimplência ── */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12 }}>

            {/* Boletos do mês */}
            <Section title={`Boletos — ${mesLabel}`} icon="bi-receipt">
              {loading ? (
                <div style={{ height:100, background:"var(--gray-50)", borderRadius:8 }}/>
              ) : !data?.boletosDoMes?.total ? (
                <div style={{ textAlign:"center", padding:"24px 0", color:"var(--gray-400)" }}>
                  <i className="bi bi-receipt" style={{ fontSize:28, display:"block", marginBottom:6 }}/>
                  <p style={{ fontSize:13 }}>Nenhum boleto gerado para {mesLabel}</p>
                  <button className="btn btn-primary btn-sm mt-2" onClick={() => onNavigate?.("cobrancas")}>
                    <i className="bi bi-plus me-1"/>Gerar Boletos
                  </button>
                </div>
              ) : (
                <div>
                  {/* Barra de progresso adimplência */}
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12,
                      color:"var(--gray-600)", marginBottom:5 }}>
                      <span>Progresso de recebimento</span>
                      <span style={{ fontWeight:700 }}>
                        {data.boletosDoMes.pagos}/{data.boletosDoMes.total} boletos
                      </span>
                    </div>
                    <div style={{ height:10, background:"var(--gray-100)", borderRadius:99 }}>
                      <div style={{ height:"100%", borderRadius:99, background:"#22C55E",
                        width:`${data.boletosDoMes.pctAdimplencia || 0}%`,
                        transition:"width .6s ease" }}/>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:4, fontSize:11 }}>
                      <span style={{ color:"#22C55E", fontWeight:600 }}>
                        {fmtM(data.boletosDoMes.vlrRecebido)} recebido
                      </span>
                      <span style={{ color:"#EF4444", fontWeight:600 }}>
                        {fmtM(data.boletosDoMes.vlrVencido)} vencido
                      </span>
                    </div>
                  </div>

                  {/* 4 status */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                    {[
                      { label:"Pagos",     val:data.boletosDoMes.pagos,     color:"#166534", bg:"#DCFCE7", icon:"bi-check-circle-fill" },
                      { label:"Em Aberto", val:data.boletosDoMes.emAberto,  color:"#1D4ED8", bg:"#EFF6FF", icon:"bi-clock-fill" },
                      { label:"Vencidos",  val:data.boletosDoMes.vencidos,  color:"#991B1B", bg:"#FEE2E2", icon:"bi-exclamation-circle-fill" },
                      { label:"Total",     val:data.boletosDoMes.total,     color:"var(--gray-600)", bg:"#F1F5F9", icon:"bi-receipt-cutoff" },
                    ].map(s => (
                      <div key={s.label} style={{ background:s.bg, borderRadius:8, padding:"10px 12px",
                        cursor:"pointer" }} onClick={() => onNavigate?.("boletos")}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                          <i className={`bi ${s.icon}`} style={{ color:s.color, fontSize:13 }}/>
                          <span style={{ fontSize:11, color:s.color, fontWeight:600 }}>{s.label}</span>
                        </div>
                        <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Gauge + inadimplência */}
            <Section title="Adimplência" icon="bi-pie-chart"
              action={<i className="bi bi-info-circle" style={{color:"var(--gray-400)",fontSize:13,cursor:"help"}}
                title="% de boletos do mês com status 'Pago' sobre o total gerado"/>}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
                {data?.boletosDoMes?.pctAdimplencia != null ? (
                  <Gauge pct={data.boletosDoMes.pctAdimplencia}/>
                ) : (
                  <div style={{ textAlign:"center", color:"var(--gray-400)", padding:"16px 0", fontSize:12 }}>
                    Sem dados de boletos
                  </div>
                )}
                {data?.inadimplencia?.qtdUnidades > 0 && (
                  <div style={{ background:"var(--bg-danger-soft)", borderRadius:8, padding:"10px 14px", width:"100%" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#991B1B", marginBottom:4,
                      display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      <span>
                        <i className="bi bi-exclamation-triangle-fill me-1"/>
                        {data.inadimplencia.qtdUnidades} unidade(s) em atraso
                      </span>
                      {data?.comparativo?.inadimplencia?.deltaPct != null && (
                        <DeltaTag pct={data.comparativo.inadimplencia.deltaPct} invertido/>
                      )}
                    </div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#991B1B" }}>
                      {fmtM(data.inadimplencia.vlrOriginal)}
                    </div>
                    <button className="btn btn-sm mt-2 w-100"
                      style={{ background:"var(--bg-danger-soft)", border:"1px solid #FECACA", color:"#991B1B", fontSize:11 }}
                      onClick={() => onNavigate?.("renegociacoes")}>
                      <i className="bi bi-arrow-repeat me-1"/>Renegociar
                    </button>
                  </div>
                )}
              </div>
            </Section>
          </div>

          {/* ── LINHA 3 — Gráfico + Contas bancárias ── */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12 }}>

            {/* Gráfico receitas x despesas */}
            <Section title="Receitas x Despesas (6 meses)" icon="bi-bar-chart-line">
              {loading ? (
                <div style={{ height:80, background:"var(--gray-50)", borderRadius:8 }}/>
              ) : (
                <>
                  <div style={{ display:"flex", gap:16, marginBottom:8 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
                      <div style={{ width:10, height:10, borderRadius:2, background:"#3B82F6" }}/>
                      <span style={{ color:"var(--gray-500)" }}>Receitas</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
                      <div style={{ width:10, height:10, borderRadius:2, background:"#EF4444" }}/>
                      <span style={{ color:"var(--gray-500)" }}>Despesas</span>
                    </div>
                    <div style={{ marginLeft:"auto", fontSize:11, color:"var(--gray-400)" }}>
                      Últimos 6 meses
                    </div>
                  </div>
                  <BarChart data={data?.grafico || []} height={80}/>

                  {/* Totais do período */}
                  <div style={{ display:"flex", gap:12, marginTop:12, paddingTop:12,
                    borderTop:"1px solid var(--gray-100)" }}>
                    <div style={{ flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#3B82F6" }}>
                        {fmtM(data?.grafico?.reduce((a,g)=>a+g.receitas,0))}
                      </div>
                      <div style={{ fontSize:10, color:"var(--gray-400)" }}>Total Receitas</div>
                    </div>
                    <div style={{ flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#EF4444" }}>
                        {fmtM(data?.grafico?.reduce((a,g)=>a+g.despesas,0))}
                      </div>
                      <div style={{ fontSize:10, color:"var(--gray-400)" }}>Total Despesas</div>
                    </div>
                    <div style={{ flex:1, textAlign:"center" }}>
                      {(()=>{
                        const saldo=(data?.grafico?.reduce((a,g)=>a+g.receitas,0)||0)-(data?.grafico?.reduce((a,g)=>a+g.despesas,0)||0);
                        return <>
                          <div style={{ fontSize:13, fontWeight:700, color:saldo>=0?"#166534":"#991B1B" }}>
                            {fmtM(saldo)}
                          </div>
                          <div style={{ fontSize:10, color:"var(--gray-400)" }}>Saldo Período</div>
                        </>;
                      })()}
                    </div>
                  </div>
                </>
              )}
            </Section>

            {/* Contas bancárias */}
            <Section title="Contas Bancárias" icon="bi-bank"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("contasBancarias")}>
                <i className="bi bi-arrow-right me-1"/>Ver todas
              </button>}>
              {loading ? (
                <div style={{ height:80, background:"var(--gray-50)", borderRadius:8 }}/>
              ) : !data?.contasBancarias?.length ? (
                <p style={{ fontSize:12.5, color:"var(--gray-400)", textAlign:"center", padding:"16px 0" }}>
                  Nenhuma conta bancária ativa
                </p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {data.contasBancarias.slice(0,4).map(cb => (
                    <div key={cb.id} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"center", padding:"8px 10px", background:"var(--gray-50)",
                      borderRadius:8, border:"1px solid var(--gray-100)" }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:12.5, color:"var(--gray-700)" }}>
                          {cb.nmConta}
                        </div>
                        <div style={{ fontSize:11, color:"var(--gray-400)" }}>{cb.banco||"—"}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontWeight:700, fontSize:13,
                          color:parseFloat(cb.saldoAtual||0)>=0?"#166534":"#991B1B" }}>
                          {fmtM(cb.saldoAtual)}
                        </div>
                        {cb.chavePix && (
                          <div style={{ fontSize:10, color:"#7C3AED" }}>
                            <i className="bi bi-qr-code me-1"/>PIX
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {data.contasBancarias.length > 4 && (
                    <div style={{ fontSize:11.5, color:"var(--gray-400)", textAlign:"center" }}>
                      +{data.contasBancarias.length - 4} conta(s)
                    </div>
                  )}
                </div>
              )}
            </Section>
          </div>

          {/* ── LINHA 4 — Ocorrências + Reservas + Renegociações + Últimas Transações ── */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>

            {/* Ocorrências */}
            <Section title={`Ocorrências${data?.ocorrencias?.total ? ` (${data.ocorrencias.total})` : ""}`} icon="bi-exclamation-circle"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("ocorrencias")}>Ver</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {data?.ocorrencias?.total > 0 ? (
                    <>
                      {[
                        { label:"Abertas",     val:data?.ocorrencias?.abertas,     color:"#991B1B", bg:"#FEE2E2" },
                        { label:"Em Andamento",val:data?.ocorrencias?.emAndamento, color:"#1D4ED8", bg:"#EFF6FF" },
                        { label:"Urgentes",    val:data?.ocorrencias?.urgentes,    color:"#C2410C", bg:"#FFF7ED" },
                        { label:"Vencidas",    val:data?.ocorrencias?.vencidas,    color:"#854D0E", bg:"#FEF9C3" },
                      ].map(s => (
                        <div key={s.label} style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"center", padding:"6px 10px", background:s.bg, borderRadius:6 }}>
                          <span style={{ fontSize:12, color:s.color }}>{s.label}</span>
                          <span style={{ fontWeight:700, fontSize:14, color:s.color }}>{s.val||0}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p style={{fontSize:12,color:"var(--gray-400)",textAlign:"center",padding:"16px 0"}}>
                      <i className="bi bi-check-circle-fill me-1" style={{color:"#16A34A"}}/>
                      Nenhuma ocorrência em aberto
                    </p>
                  )}
                </div>
              )}
            </Section>

            {/* Reservas */}
            <Section title="Reservas" icon="bi-calendar-check"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("reservas")}>Ver</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {[
                    { label:"Hoje",      val:data?.reservas?.hoje,   color:"#166534", bg:"#DCFCE7" },
                    { label:"Esta semana",val:data?.reservas?.semana, color:"#1D4ED8", bg:"#EFF6FF" },
                    { label:"Total ativas",val:data?.reservas?.total, color:"var(--gray-600)", bg:"#F1F5F9" },
                  ].map(s => (
                    <div key={s.label} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"center", padding:"6px 10px", background:s.bg, borderRadius:6 }}>
                      <span style={{ fontSize:12, color:s.color }}>{s.label}</span>
                      <span style={{ fontWeight:700, fontSize:14, color:s.color }}>{s.val||0}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Renegociações */}
            <Section title="Renegociações" icon="bi-arrow-repeat"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("renegociacoes")}>Ver</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {data?.renegociacoes?.total > 0 ? (
                    <>
                      <div style={{ textAlign:"center", padding:"8px 0" }}>
                        <div style={{ fontSize:28, fontWeight:800, color:"#7C3AED" }}>
                          {data.renegociacoes.total}
                        </div>
                        <div style={{ fontSize:11, color:"var(--gray-400)" }}>acordo(s) ativo(s)</div>
                      </div>
                      <div style={{ background:"#F5F3FF", borderRadius:6, padding:"8px 10px" }}>
                        <div style={{ fontSize:11, color:"#6D28D9" }}>Saldo a receber</div>
                        <div style={{ fontWeight:700, fontSize:14, color:"#7C3AED" }}>
                          {fmtM(data.renegociacoes.vlrSaldo)}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign:"center", padding:"16px 0", color:"var(--gray-400)" }}>
                      <i className="bi bi-check-circle" style={{ fontSize:24, display:"block", marginBottom:4 }}/>
                      <span style={{ fontSize:12 }}>Nenhum acordo ativo</span>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Últimas transações */}
            <Section title="Últimas Transações" icon="bi-clock-history"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("contasBancarias")}>Ver</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> :
               !data?.ultimasTransacoes?.length ? (
                <p style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center", padding:"16px 0" }}>
                  Nenhuma transação recente
                </p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {data.ultimasTransacoes.map((t, i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"center", padding:"5px 8px", background:"var(--gray-50)",
                      borderRadius:6, border:"1px solid var(--gray-100)" }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11.5, fontWeight:600, color:"var(--gray-700)",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {t.historico || t.nmConta}
                        </div>
                        <div style={{ fontSize:10, color:"var(--gray-400)" }}>
                          {fmtD(t.dtTransacao)} · {t.nmConta}
                        </div>
                      </div>
                      <div style={{ fontWeight:700, fontSize:12, flexShrink:0, marginLeft:8,
                        color:t.tpMovimento==="Credito"?"#166534":"#991B1B" }}>
                        {t.tpMovimento==="Credito"?"+":"-"}{fmtM(t.vlrTransacao)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* ── LINHA 5 — Assembleias + Top Devedores + Vencendo 7d ── */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>

            {/* Assembleias próximas */}
            <Section title="Assembleias Próximas" icon="bi-people"
              action={<button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("assembleias")}>Ver todas</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> :
               !data?.assembleias?.length ? (
                <p style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center", padding:"16px 0" }}>
                  <i className="bi bi-calendar-check" style={{fontSize:20,display:"block",marginBottom:4}}/>
                  Nenhuma assembleia agendada
                </p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {data.assembleias.map((a,i) => (
                    <div key={i} style={{ padding:"8px 10px", background:"var(--bg-highlight)",
                      borderRadius:8, borderLeft:"3px solid #1D4ED8" }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#1D4ED8" }}>
                        {a.titulo||a.tpAssembleia}
                      </div>
                      <div style={{ fontSize:11, color:"#1D4ED8", marginTop:2 }}>
                        <i className="bi bi-calendar3 me-1"/>
                        {(() => {
                            const raw = a.dtAssembleia;
                            const str = raw instanceof Date ? raw.toISOString() : String(raw||"");
                            const dt  = new Date(str.substring(0,10)+"T12:00:00");
                            return isNaN(dt) ? str.substring(0,10) : dt.toLocaleDateString("pt-BR");
                          })()}
                        {a.hrInicio ? ` às ${String(a.hrInicio).substring(0,5)}` : ""}
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, color:"#1D4ED8",
                        background:"var(--bg-highlight)", padding:"1px 6px", borderRadius:6 }}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Top Devedores */}
            <Section title="Top Inadimplentes" icon="bi-exclamation-circle"
              action={<button className="btn btn-outline-danger btn-sm" style={{fontSize:11}}
                onClick={() => onNavigate?.("relatorios")}>Relatório</button>}>
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> :
               !data?.topDevedores?.length ? (
                <p style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center", padding:"16px 0" }}>
                  <i className="bi bi-check-circle-fill" style={{fontSize:20,color:"#166534",display:"block",marginBottom:4}}/>
                  Sem inadimplência
                </p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                  {data.topDevedores.map((d,i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"center", padding:"6px 8px",
                      background:i===0?"#FEE2E2":i===1?"#FFF7ED":"var(--gray-50)",
                      borderRadius:6 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11.5, fontWeight:600, color:"var(--gray-700)",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          Un. {d.unidade}{d.bloco?`/Bl.${d.bloco}`:""} — {d.nmCondomino||"—"}
                        </div>
                        <div style={{ fontSize:10, color:"var(--gray-400)" }}>
                          {d.qtdBoletos} boleto(s) · {d.diasMaxAtraso} dias atraso
                        </div>
                      </div>
                      <div style={{ fontWeight:700, fontSize:12, color:"#991B1B", flexShrink:0, marginLeft:8 }}>
                        {fmtM(d.vlrTotal)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Vencendo em 7 dias */}
            <Section title="Vencendo em 7 Dias" icon="bi-clock-history">
              {loading ? <div style={{height:80,background:"var(--gray-50)",borderRadius:8}}/> : (
                <div>
                  {data?.boletosVencendo7d?.qtd > 0 ? (
                    <div>
                      <div style={{ textAlign:"center", padding:"12px 0" }}>
                        <div style={{ fontSize:36, fontWeight:800, color:"#C2410C" }}>
                          {data.boletosVencendo7d.qtd}
                        </div>
                        <div style={{ fontSize:12, color:"#C2410C", fontWeight:600 }}>
                          boleto(s) vencendo
                        </div>
                        <div style={{ fontSize:18, fontWeight:700, color:"#991B1B", marginTop:6 }}>
                          {fmtM(data.boletosVencendo7d.vlrTotal)}
                        </div>
                        <div style={{ fontSize:11, color:"var(--gray-400)" }}>total a vencer</div>
                      </div>
                      <button className="btn btn-warning btn-sm w-100 mt-2"
                        onClick={() => onNavigate?.("boletos")} style={{fontSize:12}}>
                        <i className="bi bi-eye me-1"/>Ver Boletos
                      </button>
                    </div>
                  ) : (
                    <p style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center", padding:"24px 0" }}>
                      <i className="bi bi-check-circle-fill" style={{fontSize:20,color:"#166534",display:"block",marginBottom:4}}/>
                      Nenhum boleto vencendo em 7 dias
                    </p>
                  )}
                </div>
              )}
            </Section>

          </div>

        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

